<template>
  <el-dialog
    :visible="visible"
    @update:visible="$emit('update:visible', $event)"
    custom-class="card-detail-dialog"
    :close-on-click-modal="false"
    :show-close="true"
  >
    <div class="dialog-header">
      <div class="parent-select">
        <el-select :value="columnId" size="mini" @change="$emit('column-switch', $event)">
          <el-option
            v-for="col in columns"
            :key="col.id"
            :label="col.title"
            :value="col.id"
          ></el-option>
        </el-select>
      </div>
      <div class="tip red">(設定到期日、指定成員、留下敘述、勾選完成，公司成員可共同評論)</div>
    </div>

    <div v-if="card" class="dialog-content">
      <!-- 左側主體 -->
      <div class="dialog-main">
        <div class="detail-block">
          <div class="detail-title-section">
            <i
              :class="card.completed ? 'el-icon-success checked' : 'el-icon-circle-check unchecked'"
              @click="$emit('toggle-check', card)"
            ></i>
            <el-input class="detail-title" v-model="card.title" />
          </div>

          <!-- 到期日 -->
          <div class="detail-item-block">
            <h4>到期日</h4>
            <div class="date-picker-wrapper">
              <el-date-picker
                v-model="card.dueDateTime"
                type="datetime"
                placeholder="選擇日期時間"
                size="mini"
                format="yyyy-MM-dd HH:mm:ss"
                value-format="yyyy-MM-dd HH:mm:ss"
              ></el-date-picker>
              <el-tag size="mini" type="success" v-if="card.completed">完成</el-tag>
            </div>
          </div>

          <!-- 成員清單 -->
          <div class="detail-item-block">
            <h4>成員</h4>
            <div class="member-section">
              <div class="member-list">
                <template v-if="card.members && card.members.length">
                  <el-dropdown
                    v-for="member in card.members"
                    :key="member.id"
                    trigger="click"
                    @command="cmd => handleMemberCommand(cmd, member.id)"
                  >
                    <div class="member-avatar-tag">
                      <el-avatar size="small" :src="getMember(member.id).avatar">
                        {{ getMember(member.id).name ? getMember(member.id).name.charAt(0) : 'U' }}
                      </el-avatar>
                      <span class="member-name">{{ getMember(member.id).name }}</span>
                      <i class="el-icon-arrow-down el-icon--right"></i>
                    </div>
                    <el-dropdown-menu slot="dropdown">
                      <el-dropdown-item
                        command="remove"
                        icon="el-icon-delete"
                        style="color: #f56c6c"
                      >
                        從卡片移除
                      </el-dropdown-item>
                    </el-dropdown-menu>
                  </el-dropdown>
                </template>
                <span v-else class="no-member-text">尚無指定成員</span>

                <el-popover
                  placement="bottom-start"
                  width="240"
                  trigger="click"
                  v-model="memberPopoverVisible"
                >
                  <div class="member-popover-content">
                    <div class="popover-title">成員清單</div>
                    <el-select
                      v-model="card.members"
                      multiple
                      filterable
                      value-key="id"
                      placeholder="搜尋並勾選成員"
                      size="mini"
                      style="width: 100%"
                    >
                      <el-option
                        v-for="user in memberOptions"
                        :key="user.id"
                        :label="user.name"
                        :value="{ id: user.id, name: user.name, avatar: user.avatar }"
                      >
                        <div class="member-option-item">
                          <el-avatar size="small" :src="user.avatar">{{
                            user.name ? user.name.charAt(0) : ''
                          }}</el-avatar>
                          <span>{{ user.name }}</span>
                        </div>
                      </el-option>
                    </el-select>
                  </div>
                  <el-button
                    slot="reference"
                    size="mini"
                    icon="el-icon-plus"
                    circle
                    title="新增/編輯成員"
                  ></el-button>
                </el-popover>
              </div>
            </div>
          </div>
        </div>

        <!-- 描述 -->
        <div class="detail-block">
          <h4><i class="el-icon-tickets"></i> 描述</h4>
          <el-input
            type="textarea"
            :rows="3"
            placeholder="添加詳細描述..."
            v-model="card.description"
          ></el-input>
        </div>

        <!-- 附件區域 -->
        <div class="detail-block">
          <div class="section-header">
            <h4>
              <i class="el-icon-paperclip"></i> 附件
              <span class="tip gray ml"> (可上傳最多5個檔案，每個檔案20MB以內) </span>
            </h4>
            <el-upload
              action="#"
              :auto-upload="false"
              :show-file-list="false"
              :on-change="handleFileUpload"
              :disabled="(card.attachments && card.attachments.length >= 5) || isSaving"
            >
              <el-button
                size="mini"
                icon="el-icon-plus"
                type="primary"
                plain
                :disabled="(card.attachments && card.attachments.length >= 5) || isSaving"
              >
                添加附件
              </el-button>
            </el-upload>
          </div>

          <div v-if="card.attachments && card.attachments.length" class="attachment-list">
            <div v-for="(file, index) in card.attachments" :key="index" class="attachment-card">
              <div class="attachment-preview">
                <img
                  v-if="isImage(file.name)"
                  :src="file.url || defaultImg"
                  :alt="file.name"
                  class="preview-img"
                />
                <div v-else class="file-icon-box"><i :class="getFileIcon(file.name)"></i></div>
              </div>
              <div class="attachment-info">
                <div class="attachment-name" :title="file.name">{{ file.name }}</div>
                <div class="attachment-meta">
                  <span>已添加</span>
                  <span v-if="file.createTime" class="dot-separator">•</span>
                  <span>{{ file.createTime }}</span>
                  <span v-if="file.size" class="dot-separator">•</span>
                  <span v-if="file.size">{{ file.size }} MB</span>
                </div>
              </div>
              <div class="attachment-actions">
                <el-dropdown
                  trigger="click"
                  @command="cmd => handleAttachmentCommand(cmd, index, file)"
                  :disabled="isSaving"
                >
                  <el-button size="mini" type="text" class="action-more-btn"
                    ><i class="el-icon-more"></i
                  ></el-button>
                  <el-dropdown-menu slot="dropdown">
                    <el-dropdown-item command="download" icon="el-icon-download"
                      >下載檔案</el-dropdown-item
                    >
                    <el-dropdown-item command="delete" icon="el-icon-delete" style="color: #f56c6c"
                      >刪除附件</el-dropdown-item
                    >
                  </el-dropdown-menu>
                </el-dropdown>
              </div>
            </div>
          </div>
          <div v-else class="no-attachment-text">尚無上傳附件</div>
        </div>
      </div>

      <!-- 右側評論區 -->
      <div class="dialog-sidebar">
        <div>
          <h4 style="margin: 0; margin-bottom: 1rem">
            <i class="el-icon-chat-dot-square"></i> 評論和活動
          </h4>
          <el-input
            type="textarea"
            :rows="2"
            placeholder="添加評論..."
            v-model="newComment"
          ></el-input>
          <el-button
            size="mini"
            type="primary"
            style="margin-top: 8px; width: 60px"
            @click="addComment"
            :disabled="isSaving"
            >保存</el-button
          >
        </div>
        <div style="flex: 2; overflow: auto">
          <div class="activity-list">
            <div v-for="(act, idx) in card.activities" :key="idx" class="activity-item">
              <div class="activity-content">
                <div class="activity-content-title">
                  <el-avatar size="small">
                    {{ act.userName ? act.userName.charAt(0) : '' }}
                  </el-avatar>
                  <div>{{ act.userName }}</div>
                  <div class="time">
                    {{ showCommentTime(act.createTime) }}
                  </div>
                </div>
                <div class="activity-content-text">{{ act.content }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </el-dialog>
</template>

<script>
import dayjs from 'dayjs'

export default {
  name: 'CardDetailDialog',
  props: {
    isSaving: { type: Boolean, default: false },
    visible: { type: Boolean, default: false },
    card: { type: Object, default: () => null },
    columnId: { type: Number, default: null },
    columns: { type: Array, default: () => [] },
    memberOptions: { type: Array, default: () => [] },
  },
  data() {
    return {
      newComment: '',
      memberPopoverVisible: false,
      defaultImg: 'https://placehold.co/120x80/e2e8f0/475569?text=Image',
    }
  },
  methods: {
    dayjs,
    addComment() {
      if (!this.newComment.trim()) return
      if (!this.card.activities) this.$set(this.card, 'activities', [])
      this.card.activities.unshift({
        cardID: this.card.id,
        userID: null,
        userName: '',
        content: this.newComment,
        createTime: dayjs().format('YYYY-MM-DD HH:mm:ss'),
      })
      this.newComment = ''
    },
    getMember(id) {
      return this.memberOptions.find(m => m.id === id) || { name: '未知成員', avatar: '' }
    },
    handleMemberCommand(command, memberId) {
      if (command === 'remove') {
        this.card.members = this.card.members.filter(member => member.id !== memberId)
        this.$message.success('已移除該成員')
      }
    },
    isImage(filename) {
      return /\.(jpg|jpeg|png|gif|webp)$/i.test(filename)
    },
    getFileIcon(filename) {
      if (/\.pdf$/i.test(filename)) return 'el-icon-document-checked'
      if (/\.(zip|rar|7z)$/i.test(filename)) return 'el-icon-folder-opened'
      if (/\.(doc|docx)$/i.test(filename)) return 'el-icon-document'
      return 'el-icon-paperclip'
    },
    handleFileUpload(file) {
      const rawFile = file.raw || file

      if (!rawFile) {
        this.$message.error('無法讀取檔案內容')
        return
      }

      const maxSizeInBytes = 20 * 1024 * 1024
      if (rawFile.size > maxSizeInBytes) {
        this.$message.error(
          `檔案大小不能超過 20MB（目前為 ${(rawFile.size / 1024 / 1024).toFixed(2)} MB）`
        )
        return
      }

      const fileSize = (rawFile.size / 1024 / 1024).toFixed(2)

      const reader = new FileReader()
      reader.readAsDataURL(rawFile)

      reader.onload = () => {
        const base64Url = reader.result

        const newAttachment = {
          cardID: this.card.id,
          name: rawFile.name,
          createTime: dayjs().format('YYYY-MM-DD HH:mm:ss'),
          size: fileSize,
          url: base64Url,
        }

        if (!this.card.attachments) {
          this.$set(this.card, 'attachments', [])
        }

        this.card.attachments.unshift(newAttachment)
        this.$message.success(`已成功添加附件: ${rawFile.name}`)
      }

      reader.onerror = error => {
        console.error('Base64 轉換失敗:', error)
        this.$message.error('檔案讀取失敗，請重新嘗試')
      }
    },
    handleAttachmentCommand(command, index, file) {
      if (command === 'delete') {
        this.card.attachments.splice(index, 1)
        this.$message.success(`已刪除附件 ${file.name}`)
      } else if (command === 'download') {
        if (!file || !file.url) {
          this.$message.error('無法取得檔案下載連結')
          return
        }

        try {
          const fileName = file.name || 'download'

          // 判斷是否為 Base64 或是開頭為 blob: / http: 的網址
          if (file.url.startsWith('data:') || file.url.startsWith('blob:')) {
            // --- 情況 A：未儲存的 Base64 或 Blob 本地下載 ---
            const link = document.createElement('a')
            link.href = file.url
            link.download = fileName
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            this.$message.success(`開始下載: ${fileName}`)
          } else {
            // --- 情況 B：已儲存到伺服器的遠端網址下載 ---
            // 使用 fetch 轉為 Blob 下載，可避免跨域 (CORS) 導致直接在瀏覽器開起頁面而非下載
            fetch(file.url)
              .then(res => res.blob())
              .then(blob => {
                const blobUrl = URL.createObjectURL(blob)
                const link = document.createElement('a')
                link.href = blobUrl
                link.download = fileName
                document.body.appendChild(link)
                link.click()
                document.body.removeChild(link)
                URL.revokeObjectURL(blobUrl) // 釋放記憶體
                this.$message.success(`開始下載: ${fileName}`)
              })
              .catch(err => {
                console.error('下載失敗:', err)
                // 備用方案：直接在新分頁開啟 URL
                window.open(file.url, '_blank')
              })
          }
        } catch (error) {
          console.error('下載過程出錯:', error)
          this.$message.error('下載檔案失敗')
        }
      }
    },
    showCommentTime(time) {
      if (dayjs(dayjs()).diff(time, 'minutes') < 5) return '剛剛'
      if (dayjs(dayjs()).diff(time, 'minutes') < 60)
        return `${dayjs(dayjs()).diff(time, 'minutes')} 分鐘前`
      if (dayjs(dayjs()).diff(time, 'hours') < 24)
        return `${dayjs(dayjs()).diff(time, 'hours')} 小時前`
      if (dayjs(dayjs()).diff(time, 'days') <= 3) return `${dayjs(dayjs()).diff(time, 'days')} 天前`
      return dayjs(time).format('YYYY-MM-DD')
    },
  },
}
</script>

<style scoped lang="scss">
/* 詳情彈窗專用樣式 */
::v-deep .card-detail-dialog {
  width: calc(100dvh - 128px);
  color: #333333;
  border-radius: 0.5rem;
  .el-dialog__header {
    padding: 0px !important;
    height: 0px;
  }
  .el-dialog__body {
    padding: 0px;
  }
  .dialog-header {
    display: flex;
    align-items: center;
    gap: 1rem;
    padding: 0.8rem 1.6rem;
    border-bottom: 1px solid #dddddd;
  }
}
.dialog-content {
  display: flex;
  max-height: calc(70vh - 80px);
  overflow: auto;
}
.dialog-main {
  display: flex;
  flex-direction: column;
  flex: 3;
  padding: 1.6rem;
  gap: 0.5rem;
  overflow: auto;
}
.dialog-sidebar {
  display: flex;
  flex-direction: column;
  flex: 2;
  background-color: #f3f3f3;
  border-left: 1px solid #dddddd;
  padding: 1.6rem;
  border-radius: 0 0 1rem 0;
}
.detail-title-section {
  display: flex;
  align-items: center;
  margin-bottom: 0.8rem;
  font-size: 24px;
}
.detail-item-block {
  margin-left: 2.5rem;
  h4 {
    margin-top: 1.5rem;
    margin-bottom: 0.8rem;
  }
}
.date-picker-wrapper {
  display: flex;
  align-items: center;
  gap: 6px;
}
::v-deep .detail-title input {
  font-size: 28px;
  font-weight: bold;
  border: none;
}
.detail-block {
  display: flex;
  flex-direction: column;
}
.activity-list {
  display: flex;
  flex-direction: column;
  margin-top: 1rem;
  gap: 1rem;
  .activity-content {
    .activity-content-title {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      .time {
        color: #409eff;
      }
    }
    .activity-content-text {
      margin: 0.5rem 0.5rem 0 26px;
      padding: 0.5rem;
      background-color: #fff;
      border: 1px solid #dddddd;
      border-radius: 0.5rem;
    }
  }
}
.member-list {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px;
}
.member-option-item {
  display: flex;
  align-items: center;
  gap: 0.5rem;
}
.member-avatar-tag {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 2px 8px 2px 4px;
  background-color: #f4f4f5;
  border: 1px solid #e9e9eb;
  border-radius: 16px;
  cursor: pointer;
}
.attachment-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.attachment-card {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  background-color: #f8f9fa;
  border: 1px solid #e9ecef;
  border-radius: 8px;
}
.attachment-preview {
  width: 56px;
  height: 42px;
  border-radius: 6px;
  overflow: hidden;
  background-color: #e9ecef;
  display: flex;
  align-items: center;
  justify-content: center;
}
.preview-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.attachment-info {
  flex: 1;
  min-width: 0;
}
.attachment-name {
  font-size: 13px;
  font-weight: 500;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.attachment-meta {
  font-size: 12px;
  color: #868e96;
  margin-top: 2px;
  display: flex;
  gap: 6px;
}
.section-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}
.checked {
  color: #8bc34a;
}
.unchecked {
  color: #888;
}
.tip {
  font-size: 12px;
  &.ml {
    margin-left: 0.5rem;
  }
  &.red {
    color: #ff3c3c;
  }
  &.gray {
    color: gray;
  }
}
</style>
